import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { OptionCategory } from '../../model/professional/option-category-model';
import { useCategoryFormViewModel } from '../../view-model/professional/company/category-form-view-model';
import { Button } from '../ui/button';
import FormField from '../ui/form-field';
import Switch from '../ui/switch';

interface CategoryFormProps {
  mode: 'create' | 'edit' | 'view';
  serviceId: string;
  category?: OptionCategory;
  onSuccess: (categoryId: string | null) => void;
}

const CategoryForm: React.FC<CategoryFormProps> = observer(({ 
  mode, 
  serviceId, 
  category, 
  onSuccess 
}) => {
  const viewModel = useCategoryFormViewModel();
  const { t } = useTranslation();

  useEffect(() => {
    viewModel.initializeForm(category || null);

    return () => {
      viewModel.resetForm();
    };
  }, [category?.id]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const formData = new FormData(e.currentTarget);
    
    // Extraire les données du formulaire
    const name = formData.get('name') as string;
    const description = formData.get('description') as string;
    const isRequired = formData.get('isRequired') === 'on';
    const isMultipleChoice = formData.get('isMultipleChoice') === 'on';
    const isActive = formData.get('categoryIsActive') === 'on';
    
    if (mode === 'create') {
      const result = await viewModel.submitCreateForm({
        name,
        description: description || '',
        serviceId,
        isRequired,
        isMultipleChoice
      });
      
      onSuccess(result);
    } else if (category) {
      const success = await viewModel.submitUpdateForm(
        category.id,
        {
          name,
          description: description || undefined,
          isRequired,
          isMultipleChoice
        },
        category.isActive,
        isActive
      );
      
      if (success) {
        onSuccess(category.id);
      }
    }
  };
  
  const isViewMode = mode === 'view';
  const isDisabled = isViewMode || viewModel.isSubmitting;

  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormField
        autoFocus
        id="name"
        name="name"
        label={t('services.categoryName')}
        placeholder={t('services.categoryNamePlaceholder') || ''}
        defaultValue={category?.name}
        required
        disabled={isDisabled}
      />
      
      <FormField
        id="description"
        name="description"
        label={t('services.optionDescription')}
        placeholder={t('services.categoryDescriptionPlaceholder') || ''}
        defaultValue={category?.description}
        type="textarea"
        disabled={isDisabled}
      />
      
      <div className="flex flex-col gap-2">
        <Switch
          id="isRequired"
          name="isRequired"
          label={t('services.categoryIsRequired')}
          defaultChecked={category?.isRequired}
          disabled={isDisabled}
        />
        
        <Switch
          id="isMultipleChoice"
          name="isMultipleChoice"
          label={t('services.categoryIsMultipleChoice')}
          defaultChecked={category?.isMultipleChoice}
          disabled={isDisabled}
        />
        
        {mode === 'edit' && (
          <Switch
            id="categoryIsActive"
            name="categoryIsActive"
            label={t('services.categoryIsActive')}
            defaultChecked={category?.isActive}
            disabled={isDisabled}
          />
        )}
      </div>
      
      {!isViewMode && (
        <div className="flex justify-end space-x-2 pt-4">
          <Button
            type="submit"
            disabled={isDisabled}
          >
            {viewModel.isSubmitting ? (
              <span className="inline-flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {mode === 'create' ? t('services.createCategory', 'Création...') : t('services.saveCategory', 'Enregistrement...')}
              </span>
            ) : (
              mode === 'create' 
                ? t('services.create', 'Créer') 
                : t('services.save', 'Enregistrer')
            )}
          </Button>
        </div>
      )}
      
      {viewModel.error && (
        <div className="text-red-500 text-sm mt-2">{viewModel.error}</div>
      )}
    </form>
  );
});

export default CategoryForm;
